import React from "react";
import { GithubOutlined, GlobalOutlined, LinkedinOutlined } from "@ant-design/icons";
import { theme } from "antd";
import { useResumeStore } from "../../../store/resumeStore.jsx";
import {
  formatDateRange,
  getAchievements,
  getActiveEducation,
  getActiveExperiences,
  getAboutText,
  getContactItems,
  getExperienceHighlights,
  getInitials,
  getLanguages,
  getObjectiveText,
  getPrimaryContactItems,
  getProjects,
  getSkills,
  getSocialItems,
  getSummaryText,
  normalizeExternalUrl,
  stripHtml,
} from "../resumeTemplateUtils.js";

const { useToken } = theme;

function clean(value) {
  if (value == null || value === false) return "";
  if (React.isValidElement(value)) return "";
  if (Array.isArray(value)) return value.map(clean).filter(Boolean).join(", ");
  if (typeof value === "object") {
    return clean(value.value || value.label || value.name || value.title || value.description || "");
  }

  return stripHtml(String(value))
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function renderText(value) {
  if (value == null || value === false) return "";
  if (React.isValidElement(value)) return value;
  if (typeof value === "string" || typeof value === "number") return clean(value);
  if (Array.isArray(value)) return value.map(renderText).filter(Boolean).join(", ");
  if (typeof value === "object") {
    return clean(
      value.text ||
        value.title ||
        value.description ||
        value.name ||
        value.label ||
        value.value ||
        "",
    );
  }
  return "";
}

function normalizeRenderItems(items = []) {
  return items
    .map(renderText)
    .filter((item) => React.isValidElement(item) || clean(item));
}

function alpha(color, opacity = "1f") {
  return /^#[0-9a-f]{6}$/i.test(color) ? `${color}${opacity}` : color;
}

function pickAccent(token, fallback) {
  return token?.colorPrimary || fallback;
}

function listFrom(value) {
  if (React.isValidElement(value)) return [];
  if (Array.isArray(value)) return value.flatMap(listFrom);
  if (value && typeof value === "object") {
    const preferred = clean(
      value.title ||
        value.name ||
        value.label ||
        value.value ||
        value.description ||
        value.organization ||
        value.company ||
        "",
    );
    return preferred ? [preferred] : Object.values(value).flatMap(listFrom);
  }
  return clean(value)
    .split(/\n|;|\|/g)
    .map(clean)
    .filter(Boolean);
}

function optionalList(data, keys) {
  const seen = new Set();

  return keys
    .flatMap((key) => listFrom(key.split(".").reduce((source, part) => source?.[part], data)))
    .filter((item) => {
      const normalized = item.toLowerCase();
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
}

function formatReferenceLinkLabel(rawUrl = "") {
  try {
    const hostname = new URL(normalizeExternalUrl(rawUrl)).hostname.replace(/^www\./i, "");
    const knownDomains = {
      "linkedin.com": "LinkedIn",
      "github.com": "GitHub",
      "behance.net": "Behance",
      "dribbble.com": "Dribbble",
      "medium.com": "Medium",
      "x.com": "X",
      "twitter.com": "Twitter",
    };

    if (knownDomains[hostname]) return knownDomains[hostname];

    const domainName = hostname.split(".")[0] || hostname;
    return domainName
      .split(/[-_]/g)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  } catch {
    return "";
  }
}

function useProductionTemplateModel() {
  const data = useResumeStore((state) => state.resume);
  const socials = getSocialItems(data);

  return {
    data,
    name: clean(data?.name) || "Your Name",
    role: clean(data?.roleTitle || data?.role) || "Professional Title",
    about: getAboutText(data),
    objective: getObjectiveText(data),
    summary: getSummaryText(data),
    experiences: getActiveExperiences(data),
    education: getActiveEducation(data),
    skills: getSkills(data),
    languages: getLanguages(data),
    contacts: getContactItems(data),
    primaryContacts: getPrimaryContactItems(data),
    socials,
    projects: getProjects(data, 6),
    achievements: getAchievements(data),
    certifications: optionalList(data, ["certifications", "certification", "certificates", "licenses"]),
    hobbies: optionalList(data, ["hobbies", "interests", "interestsAndHobbies"]),
    references: optionalList(data, ["references", "referees"]),
    initials: getInitials(data?.name || "CV") || "CV",
  };
}

function linkFor(value) {
  const text = clean(value);
  if (!text) return null;

  const email = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0];
  if (email) return { href: `mailto:${email}`, label: email };

  const url = text.match(/(?:https?:\/\/)?(?:www\.)?(?:linkedin\.com|github\.com|[\w.-]+\.[a-z]{2,})(?:\/[^\s,)]+)?/i)?.[0];
  if (!url) return null;

  return {
    href: normalizeExternalUrl(url),
    label: formatReferenceLinkLabel(url) || url.replace(/^https?:\/\//i, "").replace(/^www\./i, ""),
  };
}

function getSocialLabel(key = "") {
  if (key === "linkedin") return "LinkedIn";
  if (key === "github") return "GitHub";
  if (key === "twitter") return "X";
  if (key === "portfolio") return "Portfolio";
  if (key === "website") return "Website";
  if (key === "behance") return "Behance";
  if (key === "dribbble") return "Dribbble";
  return key ? key.charAt(0).toUpperCase() + key.slice(1) : "Link";
}

function SocialIconLinks({ items, accent, color = "#475569", background = "transparent", style = {}, iconOnly = false }) {
  const socialLinks = items
    .map((item) => ({ ...item, link: linkFor(item.value), label: getSocialLabel(item.key) }))
    .filter((item) => item.link);

  if (!socialLinks.length) return null;

  const icons = {
    linkedin: <LinkedinOutlined />,
    github: <GithubOutlined />,
  };

  return (
    <div
      data-section="socialLinks"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "5px 8px",
        alignItems: "center",
        marginTop: 7,
        ...style,
      }}
    >
      {socialLinks.map((item) => (
        <a
          key={item.key}
          href={item.link.href}
          target="_blank"
          rel="noreferrer"
          data-field={item.key}
          aria-label={item.label}
          title={item.label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: iconOnly ? 0 : 4,
            width: iconOnly ? 25 : "auto",
            height: iconOnly ? 25 : "auto",
            minWidth: 0,
            color,
            background,
            border: `1px solid ${alpha(accent, "44")}`,
            borderRadius: 999,
            padding: iconOnly ? 0 : "2px 7px",
            fontSize: iconOnly ? 13 : 10.8,
            fontWeight: 800,
            lineHeight: iconOnly ? 1 : 1.25,
            textDecoration: "none",
            overflowWrap: "anywhere",
            flex: "0 0 auto",
          }}
        >
          <span style={{ color: iconOnly ? color : accent, fontSize: iconOnly ? 13 : 12, lineHeight: 1 }}>
            {icons[item.key] || <GlobalOutlined />}
          </span>
          {!iconOnly && <span>{item.label}</span>}
        </a>
      ))}
    </div>
  );
}

function Shell({
  children,
  background = "#ffffff",
  color = "#172033",
  padding = 24,
  fontFamily = "Arial, Helvetica, sans-serif",
}) {
  return (
    <div
      role="document"
      data-resume-root="true"
      data-template-family="prototype-production"
      style={{
        width: "100%",
        minHeight: "100%",
        background,
        color,
        padding,
        fontFamily,
        fontSize: 12.5,
        lineHeight: 1.35,
        boxSizing: "border-box",
        overflowWrap: "break-word",
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
    >
      {children}
    </div>
  );
}

function Avatar({ model, size = 84, square = false, style = {} }) {
  if (model.data?.photo) {
    return (
      <img
        src={model.data.photo}
        alt="Profile"
        data-field="photo"
        style={{
          width: size,
          height: size,
          borderRadius: square ? 18 : "50%",
          objectFit: "cover",
          display: "block",
          ...style,
        }}
      />
    );
  }

  return (
    <div
      data-field="photo"
      aria-label="Profile initials"
      style={{
        width: size,
        height: size,
        borderRadius: square ? 18 : "50%",
        display: "grid",
        placeItems: "center",
        background: "#1f2937",
        color: "#ffffff",
        fontWeight: 800,
        fontSize: Math.max(18, size * 0.32),
        ...style,
      }}
    >
      {model.initials}
    </div>
  );
}

function Title({ children, accent, color = "#172033", size = 13, line = false, style = {} }) {
  return (
    <h2
      data-title="true"
      style={{
        margin: "0 0 8px",
        display: line ? "flex" : "block",
        alignItems: "center",
        gap: 9,
        color,
        fontSize: size,
        fontWeight: 800,
        letterSpacing: "0.08em",
        lineHeight: 1.18,
        textTransform: "uppercase",
        ...style,
      }}
    >
      <span>{children}</span>
      {line ? <span style={{ height: 1, flex: 1, background: accent, opacity: 0.55 }} /> : null}
    </h2>
  );
}

function ContactList({ items, accent, color = "#475569", compact = false, vertical = true }) {
  if (!items.length) return null;
  const icons = { location: "⌂", phone: "☎", email: "✉", linkedin: "in", github: "GH" };

  return (
    <div
      style={{
        display: vertical ? "grid" : "flex",
        flexWrap: "wrap",
        gap: compact ? "4px 10px" : "6px 14px",
        color,
        fontSize: compact ? 10.7 : 11.7,
        lineHeight: 1.25,
      }}
    >
      {items.map((item) => {
        const link = ["email", "linkedin", "github"].includes(item.key) ? linkFor(item.value) : null;
        return (
          <div
            key={item.key}
            data-section="contactItem"
            style={{
              display: "grid",
              gridTemplateColumns: "15px minmax(0, 1fr)",
              gap: 5,
              alignItems: "start",
              minWidth: 0,
            }}
          >
            <span style={{ color: accent, fontWeight: 800 }}>{icons[item.key] || "•"}</span>
            {link ? (
              <a
                href={link.href}
                target="_blank"
                rel="noreferrer"
                data-field={item.key}
                style={{ color, textDecoration: "none", overflowWrap: "anywhere" }}
              >
                {link.label}
              </a>
            ) : (
              <span data-field={item.key} style={{ overflowWrap: "anywhere" }}>
                {item.value}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Summary({ model, color = "#475569", style = {} }) {
  const entries = [
    { label: "About Me", value: model.about, field: "summary" },
    { label: "Career Objective", value: model.objective, field: "objective" },
  ].filter((entry, index, collection) => {
    if (!entry.value) return false;
    const normalized = clean(entry.value).toLowerCase();
    return collection.findIndex((candidate) => clean(candidate.value).toLowerCase() === normalized) === index;
  });

  if (!entries.length) return null;

  return (
    <div style={{ display: "grid", gap: entries.length > 1 ? 12 : 0 }}>
      {entries.map((entry) => (
        <div key={entry.field} data-section={entry.field}>
          <h2
            style={{
              margin: "0 0 5px",
              color,
              fontSize: 15.5,
              lineHeight: 1.18,
              fontWeight: 900,
              letterSpacing: "0.01em",
              textTransform: "none",
            }}
          >
            {entry.label}
          </h2>
          <p
            data-field={entry.field}
            style={{
              margin: 0,
              color,
              fontSize: 12.2,
              lineHeight: 1.38,
              ...style,
            }}
          >
            {entry.value}
          </p>
        </div>
      ))}
    </div>
  );
}

function hasProfileText(model) {
  return Boolean(model?.about || model?.objective);
}

function Chips({ items, accent, color = "#172033", background = "#ffffff", dataField = "skill" }) {
  const visibleItems = normalizeRenderItems(items);
  if (!visibleItems.length) return null;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px 6px", alignItems: "center" }}>
      {visibleItems.map((item, index) => (
        <span
          key={`${dataField}-${clean(item) || index}`}
          data-section={`${dataField}Item`}
          data-field={dataField}
          style={{
            display: "inline-flex",
            maxWidth: "100%",
            border: `1px solid ${alpha(accent, "55")}`,
            borderRadius: 30,
            padding: "3px 9px",
            background,
            color,
            fontSize: 11.1,
            fontWeight: 700,
            lineHeight: 1.18,
            overflowWrap: "anywhere",
            breakInside: "avoid",
            pageBreakInside: "avoid",
          }}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function FounderSkillTiles({ items, accent }) {
  const visibleItems = normalizeRenderItems(items);
  if (!visibleItems.length) return null;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", alignItems: "center" }}>
      {visibleItems.map((item, index) => (
        <span
          key={`founder-skill-${item}-${index}`}
          data-section="skillItem"
          data-field="skill"
          style={{
            display: "inline-flex",
            alignItems: "center",
            maxWidth: "100%",
            border: `1px solid ${alpha(accent, "55")}`,
            borderRadius: 6,
            background: "#fff7ed",
            color: "#7c2d12",
            padding: "3px 6px",
            fontSize: 10.4,
            fontWeight: 900,
            lineHeight: 1.08,
            breakInside: "avoid",
            pageBreakInside: "avoid",
            overflowWrap: "anywhere",
          }}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function Education({ items, accent, compact = false, dark = false }) {
  if (!items.length) return null;

  return (
    <div style={{ display: "grid", gap: compact ? 6 : 9 }}>
      {items.map((item, index) => {
        const dates = formatDateRange(item.from || item.startDate, item.to || item.endDate, item.years);
        const description = clean(item.description);
        const hasTitle = clean(item.degree || item.field || item.institution);
        if (!hasTitle) return null;

        return (
          <article
            key={`education-${index}`}
            className="cv-group"
            data-section="educationItem"
            style={{ minWidth: 0, breakInside: "avoid", pageBreakInside: "avoid" }}
          >
            <div
              data-field="degree"
              style={{
                color: dark ? "#ffffff" : "#172033",
                fontSize: compact ? 12.4 : 13.4,
                fontWeight: 800,
                lineHeight: 1.2,
              }}
            >
              {clean(item.degree || item.field || "Education")}
            </div>
            <div style={{ marginTop: 2, color: dark ? "#cbd5e1" : "#64748b", fontSize: compact ? 11.2 : 11.8, lineHeight: 1.3 }}>
              {item.institution ? <span data-field="institution">{item.institution}</span> : null}
              {item.field && item.field !== item.degree ? <span data-field="field"> | {item.field}</span> : null}
              {dates ? <span data-field="years"> | {dates}</span> : null}
            </div>
            {item.score ? (
              <div data-field="score" style={{ color: accent, fontSize: 11.2, fontWeight: 700, marginTop: 2 }}>
                {item.score}
              </div>
            ) : null}
            {description && description !== clean(item.degree) ? (
              <div data-field="description" style={{ color: dark ? "#cbd5e1" : "#64748b", fontSize: 11.4, lineHeight: 1.32, marginTop: 3 }}>
                {description}
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

function ExperienceCards({ items, accent, variant = "card", dark = false, numbered = false, startIndex = 0, compact = false }) {
  if (!items.length) return null;

  return (
    <div style={{ display: "grid", gap: compact ? 4 : variant === "ledger" ? 5 : 5 }}>
      {items.map((item, index) => {
        const dates = formatDateRange(item.startDate, item.endDate, item.years);
        const highlights = getExperienceHighlights(item);
        const isLedger = variant === "ledger";
        const background = dark ? "#0f172a" : isLedger ? "#ffffff" : "#ffffff";

        return (
          <article
            key={`${item.company}-${item.role}-${index}`}
            className="cv-group"
            data-section="experienceItem"
            style={{
              display: "grid",
              gridTemplateColumns: numbered ? `${compact ? 28 : 34}px minmax(0, 1fr)` : "1fr",
              gap: numbered ? (compact ? 7 : 10) : 0,
              background,
              border: `1px solid ${dark ? alpha(accent, "55") : alpha(accent, "30")}`,
              borderRadius: compact ? 10 : isLedger ? 18 : 14,
              padding: compact ? "7px 9px" : isLedger ? "12px 14px" : "11px 13px",
              boxShadow: compact || dark ? "none" : "0 8px 18px rgba(15,23,42,0.05)",
              breakInside: "auto",
              pageBreakInside: "auto",
            }}
          >
            {numbered ? (
              <div style={{ color: accent, fontSize: compact ? 11.2 : 12.5, fontWeight: 900, lineHeight: compact ? 1.25 : 1.4 }}>
                {String(startIndex + index + 1).padStart(2, "0")}
              </div>
            ) : null}
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: compact ? 6 : 8, alignItems: "baseline" }}>
                <div data-field="jobRole" style={{ color: dark ? "#f8fafc" : "#111827", fontSize: compact ? 12.9 : isLedger ? 14.6 : 13.8, fontWeight: 900, lineHeight: 1.16 }}>
                  {item.role || "Role"}
                </div>
                {dates ? (
                  <div data-field="years" style={{ color: accent, fontSize: compact ? 10.1 : 10.8, fontWeight: 800, whiteSpace: "nowrap" }}>
                    {dates}
                  </div>
                ) : null}
              </div>
              {item.company ? (
                <div data-field="company" style={{ color: dark ? "#94a3b8" : "#64748b", fontSize: compact ? 10.8 : 11.8, marginTop: compact ? 1 : 3 }}>
                  {item.company}
                </div>
              ) : null}
              {highlights.length ? (
                <ul style={{ margin: compact ? "4px 0 0" : "6px 0 0", paddingLeft: compact ? 14 : 16, color: dark ? "#d7dee8" : "#334155", fontSize: compact ? 11.1 : 11.9, lineHeight: compact ? 1.24 : 1.34 }}>
                  {highlights.map((highlight, highlightIndex) => (
                    <li key={highlightIndex} style={{ marginBottom: compact ? 1 : 2 }}>
                      {highlight}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function splitExperienceFlow(experiences, leadingCount = 2) {
  return {
    leadingExperiences: experiences.slice(0, leadingCount),
    remainingExperiences: experiences.slice(leadingCount),
  };
}

function PlainList({ items, dataField, color = "#475569", marker = "•" }) {
  const visibleItems = normalizeRenderItems(items);
  if (!visibleItems.length) return null;
  return (
    <ul style={{ margin: 0, paddingLeft: marker ? 15 : 0, color, fontSize: 11.8, lineHeight: 1.34 }}>
      {visibleItems.map((item, index) => (
        <li
          key={`${dataField}-${index}`}
          data-section={`${dataField}Item`}
          data-field={dataField}
          style={{ marginBottom: 2, listStyle: marker ? undefined : "none" }}
        >
          {item}
        </li>
      ))}
    </ul>
  );
}

function AchievementList({ items, color = "#475569" }) {
  return <PlainList items={items} dataField="achievements" color={color} />;
}

function ReferenceLinks({ model, accent, color = "#475569" }) {
  const items = [
    ...model.references.map((item) => ({ value: item, key: `reference-${item}` })),
    ...model.socials.map((item) => ({ value: item.value, key: item.key })),
  ];
  const unique = new Map();

  items.forEach((item) => {
    const link = linkFor(item.value);
    const label = link?.label || clean(item.value);
    if (label && !unique.has(label.toLowerCase())) unique.set(label.toLowerCase(), { ...item, link, label });
  });

  if (!unique.size) return null;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px 8px", fontSize: 11.3, lineHeight: 1.25 }}>
      {[...unique.values()].map((item) =>
        item.link ? (
          <a
            key={item.key}
            href={item.link.href}
            target="_blank"
            rel="noreferrer"
            data-section="referenceItem"
            data-field="reference"
            style={{ color: accent, textDecoration: "underline", textUnderlineOffset: 2 }}
          >
            {item.label}
          </a>
        ) : (
          <span key={item.key} data-section="referenceItem" data-field="reference" style={{ color }}>
            {item.label}
          </span>
        ),
      )}
    </div>
  );
}

function ProjectHighlights({ items, fallback = [], color = "#475569" }) {
  const projectItems = items
    .map((item) => {
      if (!item || typeof item !== "object") return clean(item);
      const details = Array.isArray(item.details) ? item.details.map(clean).filter(Boolean) : [];
      return clean(item.title || item.subtitle || details[0] || "");
    })
    .filter(Boolean);
  const entries = projectItems.length ? projectItems : fallback;

  return <PlainList items={entries} dataField="project" color={color} />;
}

function SkillBars({ items, accent, color = "#172033", track = "#dbe3ee", limit = 8, slim = false }) {
  const visibleItems = normalizeRenderItems(items).slice(0, limit);
  if (!visibleItems.length) return null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: slim ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: slim ? 7 : "8px 14px" }}>
      {visibleItems.map((item, index) => (
        <div key={`bar-${item}-${index}`} data-section="skillItem" data-field="skill" style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, color, fontSize: 11.4, fontWeight: 700, lineHeight: 1.15 }}>
            <span style={{ color: accent, fontSize: 11 }}>▶</span>
            <span>{item}</span>
          </div>
          <div style={{ height: slim ? 3 : 4, background: track, marginTop: 4 }}>
            <div style={{ width: `${88 - (index % 4) * 8}%`, height: "100%", background: accent }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function InlineTextList({ items, dataField = "skill", color = "#475569", separator = " / " }) {
  const visibleItems = normalizeRenderItems(items);
  if (!visibleItems.length) return null;

  return (
    <p data-field={dataField} style={{ margin: 0, color, fontSize: 12.2, lineHeight: 1.45, overflowWrap: "anywhere" }}>
      {visibleItems.map((item, index) => (
        <React.Fragment key={`${dataField}-${clean(item) || index}`}>
          {index > 0 ? separator : ""}
          {item}
        </React.Fragment>
      ))}
    </p>
  );
}

function SectionPanel({ children, title, accent, color = "#172033", style = {}, titleLine = false, titleStyle = {}, dataSection }) {
  return (
    <section className="cv-section" data-section={dataSection} style={style}>
      {title ? <Title accent={accent} color={color} line={titleLine} style={titleStyle}>{title}</Title> : null}
      {children}
    </section>
  );
}

function SecondaryFlow({ model, accent, dark = false, skip = {}, cardStyle = {}, titleColor, textColor }) {
  const color = textColor || (dark ? "#d1d5db" : "#475569");
  const headingColor = titleColor || (dark ? "#f8fafc" : "#172033");
  const chipColor = dark ? "#f8fafc" : "#172033";
  const chipBackground = dark ? alpha(accent, "12") : "#ffffff";
  const baseStyle = { marginTop: 6, ...cardStyle };

  return (
    <>
      {!skip.education && model.education.length ? (
        <SectionPanel title="Education" accent={accent} color={headingColor} style={baseStyle} dataSection="education">
          <Education items={model.education} accent={accent} compact dark={dark} />
        </SectionPanel>
      ) : null}
      {!skip.achievements && model.achievements.length ? (
        <SectionPanel title="Achievements" accent={accent} color={headingColor} style={baseStyle} dataSection="achievements">
          <AchievementList items={model.achievements} color={color} />
        </SectionPanel>
      ) : null}
      {!skip.certifications && model.certifications.length ? (
        <SectionPanel title="Certifications" accent={accent} color={headingColor} style={baseStyle} dataSection="certifications">
          <Chips items={model.certifications} accent={accent} color={chipColor} background={chipBackground} dataField="certification" />
        </SectionPanel>
      ) : null}
      {!skip.references && (model.references.length || model.socials.length) ? (
        <SectionPanel accent={accent} color={headingColor} style={baseStyle} dataSection="references">
          <ReferenceLinks model={model} accent={accent} color={color} />
        </SectionPanel>
      ) : null}
      {!skip.languages && model.languages.length ? (
        <SectionPanel title="Languages" accent={accent} color={headingColor} style={baseStyle} dataSection="languages">
          <Chips items={model.languages} accent={accent} color={chipColor} background={chipBackground} dataField="language" />
        </SectionPanel>
      ) : null}
      {!skip.hobbies && model.hobbies.length ? (
        <SectionPanel title="Hobbies" accent={accent} color={headingColor} style={baseStyle} dataSection="hobbies">
          <PlainList items={model.hobbies} dataField="hobby" color={color} />
        </SectionPanel>
      ) : null}
    </>
  );
}

function ExperienceContinuation({
  items,
  accent,
  startIndex = 0,
  title = "",
  dark = false,
  variant = "card",
  compact = false,
  style = {},
}) {
  if (!items.length) return null;

  return (
    <section className="cv-section" data-section="experience" style={{ marginTop: 2, ...style }}>
      {title ? <Title accent={accent} color={dark ? "#f8fafc" : "#172033"}>{title}</Title> : null}
      <ExperienceCards items={items} accent={accent} dark={dark} variant={variant} numbered startIndex={startIndex} compact={compact} />
    </section>
  );
}

function PrototypeTail({ model, accent, dark = false, skip = {}, cardStyle, titleColor, textColor }) {
  return (
    <SecondaryFlow
      model={model}
      accent={accent}
      dark={dark}
      skip={skip}
      cardStyle={cardStyle}
      titleColor={titleColor}
      textColor={textColor}
    />
  );
}

export function FresherOrbitTemplate() {
  const { token } = useToken();
  const model = useProductionTemplateModel();
  const accent = pickAccent(token, "#1f6feb");
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);
  const card = { background: "#ffffff", border: "1px solid #dbe3ee", borderRadius: 24, padding: 18 };

  return (
    <Shell background="#0e1628" padding={22}>
      <div style={{ background: "#f8fafc", borderRadius: 34, padding: 24, minHeight: "100%" }}>
        <header className="cv-section" data-section="header" data-cv-kind="header" style={{ background: accent, borderRadius: 28, padding: "28px 34px", color: "#ffffff", marginBottom: 24 }}>
          <h1 data-field="name" style={{ margin: 0, fontSize: 36, lineHeight: 1.02, fontWeight: 900 }}>{model.name}</h1>
          <div data-field="roleTitle" style={{ color: "#dbeafe", fontSize: 14, marginTop: 7 }}>{model.role}</div>
          <div style={{ marginTop: 10 }}>
            <ContactList items={model.primaryContacts} accent="#eff6ff" color="#eff6ff" vertical={false} compact />
            <SocialIconLinks items={model.socials} accent="#eff6ff" color="#ffffff" background="rgba(255,255,255,0.14)" iconOnly />
          </div>
        </header>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <SectionPanel title="Profile" accent={accent} color={accent} style={card} dataSection="summary">
            <Summary model={model} color="#475569" />
          </SectionPanel>
          <SectionPanel title="Stack" accent={accent} color={accent} style={card} dataSection="skills">
            <InlineTextList items={model.skills} color="#475569" />
          </SectionPanel>
        </div>

        {model.experiences.length ? (
          <SectionPanel title="Career Experience" accent={accent} color={accent} style={{ ...card, marginTop: 20 }} dataSection="experience">
            <ExperienceCards items={leadingExperiences} accent={accent} />
          </SectionPanel>
        ) : null}
        <ExperienceContinuation items={remainingExperiences} accent={accent} startIndex={leadingExperiences.length} style={card} />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {model.education.length ? (
            <SectionPanel title="Education" accent={accent} color={accent} style={card} dataSection="education">
              <Education items={model.education} accent={accent} compact />
            </SectionPanel>
          ) : null}
          {model.achievements.length ? (
            <SectionPanel title="Achievements" accent={accent} color={accent} style={card} dataSection="achievements">
              <AchievementList items={model.achievements} color="#334155" />
            </SectionPanel>
          ) : null}
        </div>
        <PrototypeTail model={model} accent={accent} cardStyle={card} skip={{ education: true, achievements: true }} />
      </div>
    </Shell>
  );
}

export function FresherAtlasTemplate() {
  const { token } = useToken();
  const model = useProductionTemplateModel();
  const accent = pickAccent(token, "#d7a365");
  const rail = "#12385f";
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);

  return (
    <Shell padding={0} background="#e7edf5">
      <div style={{ display: "grid", gridTemplateColumns: "240px minmax(0, 1fr)", minHeight: "100%" }}>
        <aside style={{ background: rail, color: "#ffffff", padding: "42px 34px" }}>
          <Avatar model={model} size={106} style={{ margin: "0 auto 34px", background: "#f8fafc", color: rail, border: `8px solid ${accent}` }} />
          <h1 data-field="name" style={{ margin: 0, color: "#ffffff", fontSize: 27, lineHeight: 1.13, fontWeight: 800 }}>
            {model.name}
          </h1>
          <div data-field="roleTitle" style={{ color: accent, fontSize: 11, letterSpacing: "0.16em", fontWeight: 800, marginTop: 9, textTransform: "uppercase" }}>
            {model.role}
          </div>
          <SocialIconLinks items={model.socials} accent={accent} color="#eef6ff" background="rgba(255,255,255,0.08)" style={{ marginTop: 12 }} iconOnly />
          <SectionPanel title="Contact" accent={accent} color={accent} style={{ marginTop: 48 }} dataSection="contact">
            <ContactList items={model.primaryContacts} accent={accent} color="#eef6ff" compact />
            <SocialIconLinks items={model.socials} accent={accent} color="#eef6ff" background="rgba(255,255,255,0.08)" iconOnly />
          </SectionPanel>
          {model.skills.length ? (
            <SectionPanel title="Skills" accent={accent} color={accent} style={{ marginTop: 46 }} dataSection="skills">
              <SkillBars items={model.skills} accent={accent} color="#ffffff" track="#335a80" slim />
            </SectionPanel>
          ) : null}
        </aside>
        <main style={{ padding: "58px 42px 38px" }}>
          <header className="cv-section" data-section="header" data-cv-kind="header" style={{ marginBottom: 16 }}>
            <h2 data-field="roleTitle" style={{ margin: 0, color: "#12263a", fontSize: 31, lineHeight: 1.05, fontWeight: 900 }}>
              {model.role}
            </h2>
            <Summary model={model} color="#526173" style={{ marginTop: 14, fontSize: 12.8 }} />
            <div style={{ height: 3, background: accent, marginTop: 24 }} />
          </header>
          {model.experiences.length ? (
            <SectionPanel title="Work Experience" accent={accent} color={rail} titleLine style={{ marginBottom: 18 }} dataSection="experience">
              <ExperienceCards items={leadingExperiences} accent={accent} />
            </SectionPanel>
          ) : null}
          <ExperienceContinuation items={remainingExperiences} accent={accent} startIndex={leadingExperiences.length} />
          {model.education.length ? (
            <SectionPanel title="Education" accent={accent} color={rail} style={{ background: "#ffffff", borderRadius: 20, padding: 16, marginTop: 18 }} dataSection="education">
              <Education items={model.education} accent={accent} compact />
            </SectionPanel>
          ) : null}
          {(model.projects.length || model.achievements.length) ? (
            <SectionPanel title="Project Highlights" accent={accent} color={rail} style={{ background: "#ffffff", borderRadius: 20, padding: 16, marginTop: 18 }} dataSection="projects">
              <ProjectHighlights items={model.projects} fallback={model.achievements} color="#263342" />
            </SectionPanel>
          ) : null}
          <PrototypeTail model={model} accent={accent} cardStyle={{ background: "#ffffff", borderRadius: 20, padding: 16, marginTop: 18 }} skip={{ education: true, achievements: true, languages: true }} />
        </main>
      </div>
    </Shell>
  );
}

export function FresherNordicTemplate() {
  const { token } = useToken();
  const model = useProductionTemplateModel();
  const accent = pickAccent(token, "#2563ad");
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);

  return (
    <Shell padding={44} background="#ffffff" fontFamily="Helvetica, Arial, sans-serif">
      <header className="cv-section" data-section="header" data-cv-kind="header" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 18, alignItems: "start", marginBottom: 18 }}>
        <div>
          <h1 data-field="name" style={{ margin: 0, color: accent, fontSize: 40, lineHeight: 1, fontWeight: 800 }}>{model.name}</h1>
          <div data-field="roleTitle" style={{ color: "#5e6876", fontSize: 17, marginTop: 5 }}>{model.role}</div>
          <div style={{ marginTop: 10 }}>
            <ContactList items={model.primaryContacts} accent={accent} color="#8a94a3" vertical={false} compact />
            <SocialIconLinks items={model.socials} accent={accent} color="#5e6876" iconOnly />
          </div>
        </div>
        <Avatar model={model} size={76} style={{ background: "#f8fafc", color: accent, border: `2px solid ${accent}` }} />
      </header>
      {hasProfileText(model) ? (
        <SectionPanel title="Profile" accent="#e6eaf0" color={accent} titleLine style={{ marginBottom: 10 }} dataSection="summary">
          <Summary model={model} color="#475569" style={{ fontSize: 13.3 }} />
        </SectionPanel>
      ) : null}
      {model.experiences.length ? (
        <SectionPanel title="Work Experience" accent="#e6eaf0" color={accent} titleLine style={{ marginBottom: 8 }} dataSection="experience">
          <ExperienceCards items={leadingExperiences} accent={accent} />
        </SectionPanel>
      ) : null}
      <ExperienceContinuation items={remainingExperiences} accent={accent} startIndex={leadingExperiences.length} />
      {model.skills.length ? (
        <SectionPanel title="Skills" accent="#e6eaf0" color={accent} titleLine style={{ marginBottom: 10 }} dataSection="skills">
          <InlineTextList items={model.skills} color="#475569" />
        </SectionPanel>
      ) : null}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {model.education.length ? (
          <SectionPanel title="Education" accent="#e6eaf0" color={accent} titleLine dataSection="education">
            <Education items={model.education} accent={accent} compact />
          </SectionPanel>
        ) : null}
        {model.achievements.length ? (
          <SectionPanel title="Achievements" accent="#e6eaf0" color={accent} titleLine dataSection="achievements">
            <AchievementList items={model.achievements} color="#475569" />
          </SectionPanel>
        ) : null}
      </div>
      <PrototypeTail model={model} accent={accent} skip={{ education: true, achievements: true }} />
    </Shell>
  );
}

export function FresherMintGridTemplate() {
  const { token } = useToken();
  const model = useProductionTemplateModel();
  const accent = pickAccent(token, "#222222");
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);

  return (
    <Shell background="#fdfdfb" padding={32} fontFamily="'Times New Roman', Georgia, serif">
      <div style={{ border: "1px solid #d9d9d4", background: "#ffffff", padding: "20px 20px", minHeight: "100%" }}>
        <header className="cv-section" data-section="header" data-cv-kind="header" style={{ borderBottom: `1px solid ${accent}`, paddingBottom: 16, marginBottom: 18, display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 18, alignItems: "start" }}>
          <div>
            <h1 data-field="name" style={{ margin: 0, color: "#1b1b1b", fontSize: 42, lineHeight: 1, fontWeight: 700 }}>{model.name}</h1>
            <div data-field="roleTitle" style={{ color: "#525252", fontFamily: "Arial, Helvetica, sans-serif", fontSize: 13, letterSpacing: "0.14em", marginTop: 8, textTransform: "uppercase" }}>{model.role}</div>
            <div style={{ marginTop: 12, fontFamily: "Arial, Helvetica, sans-serif" }}>
              <ContactList items={model.primaryContacts} accent={accent} color="#666666" vertical={false} compact />
              <SocialIconLinks items={model.socials} accent={accent} color="#444444" iconOnly />
            </div>
          </div>
          <Avatar model={model} size={74} style={{ background: "#ffffff", color: accent, border: `1px solid ${accent}` }} />
        </header>
        {hasProfileText(model) ? (
          <SectionPanel title="Professional Summary" accent={accent} color="#222222" style={{ marginBottom: 10 }} dataSection="summary">
            <Summary model={model} color="#444444" style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: 13 }} />
          </SectionPanel>
        ) : null}
        {model.experiences.length ? (
          <SectionPanel title="Experience" accent={accent} color="#222222" style={{ marginBottom: 8 }} dataSection="experience">
            <ExperienceCards items={leadingExperiences} accent={accent} />
          </SectionPanel>
        ) : null}
        <ExperienceContinuation items={remainingExperiences} accent={accent} startIndex={leadingExperiences.length} />
        {model.education.length ? (
          <SectionPanel title="Education" accent={accent} color="#222222" style={{ marginBottom: 10 }} dataSection="education">
            <Education items={model.education} accent={accent} compact />
          </SectionPanel>
        ) : null}
        {model.skills.length ? (
          <SectionPanel title="Skills" accent={accent} color="#222222" style={{ marginBottom: 10 }} dataSection="skills">
            <InlineTextList items={model.skills} color="#444444" separator=", " />
          </SectionPanel>
        ) : null}
        {model.achievements.length ? (
          <SectionPanel title="Achievements" accent={accent} color="#222222" style={{ marginBottom: 10 }} dataSection="achievements">
            <AchievementList items={model.achievements} color="#444444" />
          </SectionPanel>
        ) : null}
        <PrototypeTail model={model} accent={accent} textColor="#444444" skip={{ education: true, achievements: true, languages: true, hobbies: true }} />
      </div>
    </Shell>
  );
}

export function FresherLaunchDeckTemplate() {
  const { token } = useToken();
  const model = useProductionTemplateModel();
  const accent = pickAccent(token, "#f97316");
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences, 1);
  const founderGap = 6;
  const founderTitleStyle = { marginBottom: 5, fontSize: 12.4, lineHeight: 1.08 };
  const lightCard = { background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 12, padding: 8 };

  return (
    <Shell background="#fff7ed" padding={12}>
      <div style={{ background: "#ffffff", borderRadius: 20, padding: 12, minHeight: "100%" }}>
        <header className="cv-section" data-section="header" data-cv-kind="header" style={{ background: "#111827", borderRadius: 16, padding: "16px 20px", color: "#ffffff", marginBottom: 10 }}>
          <h1 data-field="name" style={{ margin: 0, color: "#ffffff", fontSize: 33, lineHeight: 0.98, fontWeight: 900 }}>{model.name}</h1>
          <div data-field="roleTitle" style={{ color: "#fdba74", fontSize: 12.2, marginTop: 4 }}>{model.role}</div>
          <div style={{ marginTop: 5 }}>
            <ContactList items={model.primaryContacts} accent="#fdba74" color="#e5e7eb" vertical={false} compact />
            <SocialIconLinks items={model.socials} accent="#fdba74" color="#ffffff" background="rgba(255,255,255,0.08)" iconOnly />
          </div>
        </header>
        {hasProfileText(model) ? (
          <section className="cv-section" data-section="summary" style={{ marginBottom: founderGap }}>
            <Title accent={accent} color={accent} style={founderTitleStyle}>01 Value Proposition</Title>
            <Summary model={model} color="#4b5563" style={{ fontSize: 12.4, lineHeight: 1.26 }} />
          </section>
        ) : null}

        {model.experiences.length ? (
          <SectionPanel title="Experience" accent={accent} color={accent} style={{ ...lightCard, marginTop: founderGap }} titleStyle={founderTitleStyle} dataSection="experience">
            <ExperienceCards items={leadingExperiences} accent={accent} compact />
          </SectionPanel>
        ) : null}
        {model.skills.length ? (
          <SectionPanel title="Skills" accent={accent} color={accent} style={{ ...lightCard, marginTop: founderGap }} titleStyle={founderTitleStyle} dataSection="skills">
            <FounderSkillTiles items={model.skills} accent={accent} />
          </SectionPanel>
        ) : null}

        <ExperienceContinuation
          items={remainingExperiences}
          accent={accent}
          startIndex={leadingExperiences.length}
          compact
          style={{ ...lightCard, marginTop: founderGap }}
        />

        {model.education.length ? (
          <SectionPanel title="Education" accent={accent} color={accent} style={{ ...lightCard, marginTop: founderGap }} titleStyle={founderTitleStyle} dataSection="education">
            <Education items={model.education} accent={accent} compact />
          </SectionPanel>
        ) : null}
        {model.achievements.length ? (
          <SectionPanel title="Achievements" accent={accent} color={accent} style={{ ...lightCard, marginTop: founderGap }} titleStyle={founderTitleStyle} dataSection="achievements">
            <AchievementList items={model.achievements} color="#374151" />
          </SectionPanel>
        ) : null}
        <PrototypeTail model={model} accent={accent} cardStyle={{ ...lightCard, marginTop: founderGap }} skip={{ education: true, achievements: true }} />
      </div>
    </Shell>
  );
}

export function ExperiencedExecutiveOrbitTemplate() {
  const { token } = useToken();
  const model = useProductionTemplateModel();
  const accent = pickAccent(token, "#a66b2f");
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);
  const panel = { background: "#ffffff", border: "1px solid #eadfcf", borderRadius: 20, padding: 12 };

  return (
    <Shell background="#f7f4ee" padding={24}>
      <div style={{ background: "#fffdf8", border: "1px solid #e4d9c7", borderRadius: 30, padding: "20px 10px", minHeight: "100%", position: "relative" }}>
        <div
          data-pdf-preserve-absolute="true"
          style={{
            position: "absolute",
            right: 48,
            top: 40,
            width: 116,
            height: 116,
            borderRadius: "50%",
            background: "#23364f",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Avatar model={model} size={88} style={{ background: "#fffdf8", color: "#23364f" }} />
        </div>
        <header className="cv-section" data-section="header" data-cv-kind="header" style={{ paddingRight: 148, marginBottom: 18 }}>
          <h1 data-field="name" style={{ margin: 0, color: "#1f2933", fontSize: 40, fontWeight: 900, lineHeight: 1.02 }}>{model.name}</h1>
          <div data-field="roleTitle" style={{ color: accent, fontSize: 14, letterSpacing: "0.16em", fontWeight: 800, marginTop: 10, textTransform: "uppercase" }}>{model.role}</div>
          <div style={{ marginTop: 14 }}>
            <ContactList items={model.primaryContacts} accent={accent} color="#5d6470" vertical={false} compact />
            <SocialIconLinks items={model.socials} accent={accent} color="#5d6470" iconOnly />
          </div>
        </header>
        {hasProfileText(model) ? (
          <section className="cv-section" data-section="summary" style={{ background: "#27384d", borderRadius: 22, padding: "14px 18px", marginBottom: 12 }}>
            <Title accent={accent} color="#f4c27a">Profile</Title>
            <Summary model={model} color="#ffffff" style={{ fontSize: 13.2 }} />
          </section>
        ) : null}
        {model.experiences.length ? (
          <SectionPanel title="01 Career Experience" accent={accent} color={accent} style={{ marginBottom: 8 }} dataSection="experience">
            <ExperienceCards items={leadingExperiences} accent={accent} variant="ledger" numbered />
          </SectionPanel>
        ) : null}
        <ExperienceContinuation items={remainingExperiences} accent={accent} startIndex={leadingExperiences.length} variant="ledger" />
        <SectionPanel title="02 Skills" accent={accent} color={accent} style={{ marginBottom: 10 }} dataSection="skills">
          <Chips items={model.skills} accent="#27384d" color="#ffffff" background="#27384d" />
        </SectionPanel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {model.education.length ? (
            <SectionPanel title="Education" accent={accent} color={accent} style={panel} dataSection="education">
              <Education items={model.education} accent={accent} compact />
            </SectionPanel>
          ) : null}
          {model.achievements.length ? (
            <SectionPanel title="Achievements" accent={accent} color={accent} style={panel} dataSection="achievements">
              <AchievementList items={model.achievements} color="#27384d" />
            </SectionPanel>
          ) : null}
        </div>
        <PrototypeTail model={model} accent={accent} cardStyle={panel} skip={{ education: true, achievements: true }} />
      </div>
    </Shell>
  );
}

export function ExperiencedLedgerTimelineTemplate() {
  const { token } = useToken();
  const model = useProductionTemplateModel();
  const accent = pickAccent(token, "#111111");
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);

  return (
    <Shell background="#fbfaf7" padding={30} fontFamily="Arial, Helvetica, sans-serif">
      <div style={{ background: "#ffffff", border: `2px solid ${accent}`, padding: "28px 34px", minHeight: "100%" }}>
        <header className="cv-section" data-section="header" data-cv-kind="header" style={{ borderBottom: `2px solid ${accent}`, paddingBottom: 16, marginBottom: 16 }}>
          <div style={{ color: accent, fontSize: 14, fontWeight: 900 }}>RESUME</div>
          <h1 data-field="name" style={{ margin: "24px 0 0", color: accent, fontSize: 46, lineHeight: 1, fontWeight: 900 }}>{model.name}</h1>
          <div data-field="roleTitle" style={{ display: "inline-block", marginTop: 14, background: accent, color: "#ffffff", padding: "7px 16px", fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>{model.role}</div>
          <div style={{ marginTop: 22 }}>
            <ContactList items={model.primaryContacts} accent={accent} color="#333333" vertical={false} compact />
            <SocialIconLinks items={model.socials} accent={accent} color="#333333" iconOnly />
          </div>
        </header>
        {model.experiences.length ? (
          <SectionPanel title="Experience Ledger" accent={accent} color={accent} style={{ borderBottom: `2px solid ${accent}`, paddingBottom: 22, marginBottom: 22 }} dataSection="experience">
            <ExperienceCards items={leadingExperiences} accent={accent} variant="ledger" numbered />
          </SectionPanel>
        ) : null}
        <ExperienceContinuation items={remainingExperiences} accent={accent} startIndex={leadingExperiences.length} variant="ledger" />
        {model.skills.length ? (
          <SectionPanel title="Skills" accent={accent} color={accent} style={{ borderBottom: `1px solid ${accent}`, paddingBottom: 22, marginBottom: 22 }} dataSection="skills">
            <InlineTextList items={model.skills} color="#333333" separator="     " />
          </SectionPanel>
        ) : null}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {model.education.length ? (
            <SectionPanel title="Education" accent={accent} color={accent} dataSection="education">
              <Education items={model.education} accent={accent} compact />
            </SectionPanel>
          ) : null}
          {model.achievements.length ? (
            <SectionPanel title="Achievements" accent={accent} color={accent} dataSection="achievements">
              <AchievementList items={model.achievements} color="#333333" />
            </SectionPanel>
          ) : null}
        </div>
        <PrototypeTail model={model} accent={accent} textColor="#333333" skip={{ education: true, achievements: true }} />
      </div>
    </Shell>
  );
}

export function ExperiencedGlassBentoTemplate() {
  const { token } = useToken();
  const model = useProductionTemplateModel();
  const accent = pickAccent(token, "#2563eb");
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);
  const card = { background: "rgba(255,255,255,0.9)", border: "1px solid #dbeafe", borderRadius: 28, padding: 20 };

  return (
    <Shell background="linear-gradient(135deg, #eef7ff, #f3efe8)" padding={36}>
      <header className="cv-section" data-section="header" data-cv-kind="header" style={{ marginBottom: 16 }}>
        <h1 data-field="name" style={{ margin: 0, color: "#0f172a", fontSize: 40, lineHeight: 1.02, fontWeight: 900 }}>{model.name}</h1>
        <div data-field="roleTitle" style={{ color: "#475569", fontSize: 14, marginTop: 8 }}>{model.role} | React.js | Product UI | PDF Rendering</div>
        <div style={{ marginTop: 12 }}>
          <ContactList items={model.primaryContacts} accent={accent} color="#475569" vertical={false} compact />
          <SocialIconLinks items={model.socials} accent={accent} color="#334155" background="rgba(255,255,255,0.72)" iconOnly />
        </div>
      </header>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 22, marginBottom: 24 }}>
        <SectionPanel title="Profile" accent={accent} color={accent} style={card} dataSection="summary">
          <Summary model={model} color="#334155" />
        </SectionPanel>
        <section className="cv-section" data-section="metrics" style={{ ...card, background: "#0f172a", color: "#ffffff" }}>
          <div style={{ color: "#ffffff", fontSize: 38, fontWeight: 900 }}>{model.data?.totalExperience || model.data?.relevantExperience || "8"}+</div>
          <div style={{ color: "#bae6fd", fontSize: 12.5 }}>Years Experience</div>
        </section>
      </div>
      {model.experiences.length ? (
        <SectionPanel title="Experience" accent={accent} color={accent} style={{ ...card, marginBottom: 24 }} dataSection="experience">
          <ExperienceCards items={leadingExperiences} accent={accent} />
        </SectionPanel>
      ) : null}
      <ExperienceContinuation items={remainingExperiences} accent={accent} startIndex={leadingExperiences.length} style={card} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        {model.skills.length ? (
          <SectionPanel title="Skills" accent={accent} color={accent} style={card} dataSection="skills">
            <InlineTextList items={model.skills} color="#334155" separator="  " />
          </SectionPanel>
        ) : null}
        {model.education.length ? (
          <SectionPanel title="Education" accent={accent} color={accent} style={card} dataSection="education">
            <Education items={model.education} accent={accent} compact />
          </SectionPanel>
        ) : null}
      </div>
      {model.achievements.length ? (
        <SectionPanel title="Achievements" accent={accent} color={accent} style={{ ...card, marginTop: 24 }} dataSection="achievements">
          <AchievementList items={model.achievements} color="#334155" />
        </SectionPanel>
      ) : null}
      <PrototypeTail model={model} accent={accent} cardStyle={card} skip={{ education: true, achievements: true }} />
    </Shell>
  );
}

export function ExperiencedNeonConsoleTemplate() {
  const { token } = useToken();
  const model = useProductionTemplateModel();
  const accent = pickAccent(token, "#00d4c8");
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);

  return (
    <Shell background="#050607" color="#e5e7eb" padding={34}>
      <header className="cv-section" data-section="header" data-cv-kind="header" style={{ display: "grid", gridTemplateColumns: "160px minmax(0, 1fr)", gap: 34, alignItems: "center", marginBottom: 46, borderTop: `2px solid ${accent}`, paddingTop: 20 }}>
        <Avatar model={model} size={128} style={{ background: "#15191f", border: `1px solid ${accent}` }} />
        <div>
          <h1 data-field="name" style={{ margin: 0, color: "#ffffff", fontSize: 42, lineHeight: 1, fontWeight: 900 }}>{model.name}</h1>
          <div data-field="roleTitle" style={{ color: accent, fontSize: 14, letterSpacing: "0.18em", fontWeight: 800, marginTop: 8, textTransform: "uppercase" }}>{model.role}</div>
          <div style={{ border: `1px solid ${accent}`, padding: "6px 12px", marginTop: 16 }}>
            <ContactList items={model.primaryContacts} accent={accent} color="#e5f9f7" vertical={false} compact />
            <SocialIconLinks items={model.socials} accent={accent} color="#e5f9f7" background="rgba(0,212,200,0.08)" iconOnly />
          </div>
        </div>
      </header>
      <div style={{ display: "grid", gridTemplateColumns: "190px minmax(0, 1fr)", gap: 34 }}>
        <aside>
          {model.skills.length ? (
            <SectionPanel title="Expertise" accent={accent} color={accent} style={{ marginBottom: 42 }} dataSection="skills">
              <SkillBars items={model.skills} accent={accent} color="#cbd5e1" track="#334155" slim />
            </SectionPanel>
          ) : null}
          {model.languages.length ? (
            <SectionPanel title="Language" accent={accent} color={accent} style={{ marginBottom: 42 }} dataSection="languages">
              <SkillBars items={model.languages} accent={accent} color="#cbd5e1" track="#334155" slim />
            </SectionPanel>
          ) : null}
          <SectionPanel title="Contact" accent={accent} color={accent} dataSection="contact">
            <ReferenceLinks model={model} accent={accent} color="#cbd5e1" />
          </SectionPanel>
        </aside>
        <main>
          {model.experiences.length ? (
            <SectionPanel title="Work Experience" accent={accent} color={accent} style={{ marginBottom: 10 }} dataSection="experience">
              <ExperienceCards items={leadingExperiences} accent={accent} dark numbered />
            </SectionPanel>
          ) : null}
          <ExperienceContinuation items={remainingExperiences} accent={accent} startIndex={leadingExperiences.length} dark />
          <PrototypeTail model={model} accent={accent} dark skip={{ languages: true, references: true }} />
        </main>
      </div>
    </Shell>
  );
}

export function ExperiencedEditorialSerifTemplate() {
  const { token } = useToken();
  const model = useProductionTemplateModel();
  const accent = pickAccent(token, "#9a6738");
  const { leadingExperiences, remainingExperiences } = splitExperienceFlow(model.experiences);

  return (
    <Shell background="#f4efe6" padding={40} fontFamily="Georgia, 'Times New Roman', serif">
      <header className="cv-section" data-section="header" data-cv-kind="header" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 210px", gap: 18, borderBottom: "1px solid #1d1a16", paddingBottom: 18, marginBottom: 16 }}>
        <div>
          <h1 data-field="name" style={{ margin: 0, color: "#1d1a16", fontSize: 54, lineHeight: 0.96, fontWeight: 700 }}>{model.name}</h1>
        </div>
        <div style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
          <div data-field="roleTitle" style={{ color: accent, fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase" }}>{model.role}</div>
          <div style={{ marginTop: 12 }}>
            <ContactList items={model.primaryContacts} accent={accent} color="#5f5a52" compact />
            <SocialIconLinks items={model.socials} accent={accent} color="#5f5a52" iconOnly />
          </div>
        </div>
      </header>
      {hasProfileText(model) ? (
        <section className="cv-section" data-section="summary" style={{ marginBottom: 12 }}>
          <Title accent={accent} color={accent}>Profile</Title>
          <Summary model={model} color="#2d2923" style={{ fontSize: 17, lineHeight: 1.45 }} />
        </section>
      ) : null}
      {model.experiences.length ? (
        <section className="cv-section" data-section="experience" style={{ marginBottom: 26 }}>
          <ExperienceCards items={leadingExperiences} accent={accent} variant="ledger" />
        </section>
      ) : null}
      <ExperienceContinuation items={remainingExperiences} accent={accent} startIndex={leadingExperiences.length} variant="ledger" title="Selected Work Continued" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 10, fontFamily: "Arial, Helvetica, sans-serif" }}>
        {model.skills.length ? (
          <SectionPanel title="Skills" accent={accent} color={accent} dataSection="skills">
            <InlineTextList items={model.skills} color="#2d2923" />
          </SectionPanel>
        ) : null}
        {model.education.length ? (
          <SectionPanel title="Education" accent={accent} color={accent} dataSection="education">
            <Education items={model.education} accent={accent} compact />
          </SectionPanel>
        ) : null}
      </div>
      <PrototypeTail model={model} accent={accent} textColor="#2d2923" skip={{ education: true }} />
    </Shell>
  );
}
